"use client";

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@/components/ui/modal";
import { Icon } from "@/components/ui/icon";
import { formatLongDate } from "@/lib/date/format-long-date";
import {
  useCreditBalance,
  useSubscription,
} from "../hooks/use-subscription";
import { UsageCard } from "./manage/usage-card";

interface CreditBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Render the live usage panel (settings-style bar + reset date) above the
   * explainer. Used by the navbar credits chip; the settings page keeps the
   * plain explainer since the usage bar already lives on that page.
   */
  showUsage?: boolean;
}

export function CreditBreakdownModal({
  open,
  onOpenChange,
  showUsage = false,
}: CreditBreakdownModalProps) {
  const { data: credits } = useCreditBalance();
  const { data: subscription } = useSubscription();

  const planSlug = subscription?.plan.slug ?? "free";
  const resetDate = formatLongDate(credits?.period_end);
  const resetLine =
    planSlug === "free"
      ? "Free plan credits are a one-time allocation — they don't reset."
      : resetDate
        ? `Your credits reset on ${resetDate}.`
        : null;

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm" className="sm:max-w-[500px]">
        <ModalHeader title="Credit Breakdown" iconName="IconCoin1" />
        <ModalBody className="p-0">
          {/* Visual area with illustration */}
          <div>
            <img
              src="/plan-icons/arbi-credit-breakdown.png"
              alt="Credit breakdown"
              className="w-full h-auto block"
            />
          </div>

          {showUsage && (
            <div className="flex flex-col gap-2 px-6 pt-6">
              <UsageCard
                credits={credits ?? null}
                subscription={subscription ?? null}
              />
              {resetLine && (
                <span className="flex items-center gap-1.5 text-xs text-contrast/50 dark:text-white/50">
                  <Icon
                    name="IconClock"
                    size={13}
                    className="shrink-0 text-contrast/40 dark:text-white/40"
                  />
                  {resetLine}
                </span>
              )}
            </div>
          )}

          {/* Text area */}
          <div className="p-6">
            <span className="text-xs text-[#ffaf53] tracking-[-0.12px]">
              Credits
            </span>
            <h3 className="text-base font-medium text-white tracking-[-0.16px] mt-1.5">
              What are Arbi credits?
            </h3>
            <p className="text-sm text-white/80 tracking-[-0.14px] mt-1.5">
              Credits give you access to emails, phone numbers, signals, and
              exports helping you reach talent in the right place with
              personalized precision.
            </p>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
